import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Phone, X } from "lucide-react";

type Props = {
    phoneNumber: string;
};

export function PhoneAdvice({ phoneNumber }: Props) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const previouslyFocused = useRef<Element | null>(null);

    useEffect(() => {
        const node = dialogRef.current;
        if (!node) return;
        if (open) {
            previouslyFocused.current = document.activeElement;
            if (!node.open) node.showModal();
            node.focus();
        } else if (node.open) {
            node.close();
            const prev = previouslyFocused.current;
            if (prev instanceof HTMLElement) prev.focus();
        }
    }, [open]);

    const iconCircle = (
        <div className="size-8 shrink-0 rounded-full bg-primary-light flex items-center justify-center">
            <Phone size={16} className="text-primary-dark" aria-hidden />
        </div>
    );

    return (
        <>
            <a
                href={`tel:${phoneNumber}`}
                className="hidden sm:flex items-center gap-3"
            >
                {iconCircle}
                <div>
                    <p className="text-sm font-semibold text-gray-900 leading-none">
                        {t("bottomBar.adviceTitle")}
                        <span className="ml-2 text-sm font-semibold text-primary underline whitespace-nowrap">
                            {phoneNumber}
                        </span>
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {t("bottomBar.adviceSubtitle")}
                    </p>
                </div>
            </a>

            <button
                type="button"
                onClick={() => setOpen(true)}
                className="flex items-center sm:hidden"
                aria-label={t("bottomBar.callUs")}
                data-testid="phone-advice-trigger"
            >
                {iconCircle}
            </button>

            <dialog
                ref={dialogRef}
                tabIndex={-1}
                data-testid="phone-advice-dialog"
                aria-labelledby="phone-advice-title"
                onCancel={(event) => {
                    event.preventDefault();
                    setOpen(false);
                }}
                onClick={(event) => {
                    if (event.target === dialogRef.current) setOpen(false);
                }}
                className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl p-0 shadow-xl backdrop:bg-black/40 focus:outline-none"
            >
                <div className="w-[min(360px,90vw)] p-6 flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-4">
                        <h2
                            id="phone-advice-title"
                            className="text-xl font-bold text-gray-900"
                        >
                            {t("bottomBar.adviceTitle")}
                        </h2>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label={t("bottomBar.closeDialog")}
                            className="shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                            <X size={20} aria-hidden />
                        </button>
                    </div>
                    <p className="text-sm text-gray-600">
                        {t("bottomBar.adviceSubtitle")}
                    </p>
                    <a
                        href={`tel:${phoneNumber}`}
                        className="flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-contrast-text hover:bg-primary-dark cursor-pointer"
                    >
                        <Phone size={16} aria-hidden />
                        {phoneNumber}
                    </a>
                </div>
            </dialog>
        </>
    );
}
